import { inject, nextTick, reactive, type Ref } from 'vue'
import {
  actionIdsEqual,
  resolveActionRegistryEntry,
} from '@/common/utils/resolveActionRegistryEntry'
import { findElementPageState } from '@/common/utils/resolveActionPageState'
import { shouldApplyIncomingElementVisibility } from '@/common/utils/visibilityStatePatches'

function coerceStateBoolean(value: unknown): boolean | undefined {
  if (value === undefined || value === null) return undefined
  if (typeof value === 'boolean') return value
  const t = String(value).trim().toLowerCase()
  if (t === 'true' || t === '1') return true
  if (t === 'false' || t === '0') return false
  return Boolean(value)
}

/** Последний changed_data.fields.fields по pagesData */
const lastChangedFieldsOverlayBySlot = new Map<string, Record<string, any>>()
const pendingElementRetryTimers = new Map<string, ReturnType<typeof setTimeout>>()
const pendingElementRetryCounts = new Map<string, number>()

function fieldEntryValueEqual(a: any, b: any): boolean {
  if (a === b) return true
  if (a == null || b == null) return a === b
  if (typeof a !== 'object' || typeof b !== 'object') return a === b
  const aHasValue = Object.prototype.hasOwnProperty.call(a, 'value')
  const bHasValue = Object.prototype.hasOwnProperty.call(b, 'value')
  if (aHasValue || bHasValue) {
    const aValue = aHasValue ? a.value : a
    const bValue = bHasValue ? b.value : b
    if (aValue === bValue) {
      const aType = aHasValue ? String(a.type ?? '') : ''
      const bType = bHasValue ? String(b.type ?? '') : ''
      return !aType || !bType || aType === bType
    }
    try {
      return JSON.stringify(aValue) === JSON.stringify(bValue)
    } catch {
      return false
    }
  }
  try {
    return JSON.stringify(a) === JSON.stringify(b)
  } catch {
    return false
  }
}

function shallowFieldMapEqual(a?: Record<string, any>, b?: Record<string, any>): boolean {
  if (a === b) return true
  if (!a || !b) return false
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  if (aKeys.length !== bKeys.length) return false
  return aKeys.every((key) => fieldEntryValueEqual(a[key], b[key]))
}

function isHideOnlyUnregisteredActionState(state: ElementState): boolean {
  return (
    coerceStateBoolean(state.is_visible) === false &&
    state.is_enabled === undefined &&
    state.is_editable === undefined &&
    (state as { title?: unknown }).title === undefined
  )
}

/** Единый ключ страницы вкладки для карты видимости (id из разметки). */
export function normalizePageTabId(pageId: string | number | null | undefined | { _text?: string }): string {
  if (pageId == null || pageId === '') return ''
  if (typeof pageId === 'object' && pageId !== null && '_text' in pageId) {
    return String((pageId as { _text?: unknown })._text ?? '')
  }
  return String(pageId)
}

import { FormProviderKey } from '@/components/platform/injection-keys'
import {
  type ElementState,
  type TextFontStyle,
  type BackgroundColorStyle,
  type CustomPropertyName,
  type CustomPropertyType,
  usePageData,
  CustomPropertiesKeys,
} from './usePageData'
import { applyOpenSuggesterFromStates } from '@/common/utils/applyOpenSuggesterFromStates'
import { mergePageFieldMaps, overlayAuthoritativeFieldKeys } from '@/common/utils/mergePageFieldMaps'
import { isNonRegistryLayoutActionState } from '@/common/utils/filterStatesForPageLayout'
import {
  getDialogCloseChangedFieldsFlat,
  pickDialogCloseParentMergeFlatFields,
} from '@/common/utils/dialogCloseParentMergeScope'
import type { StateData } from '@/services/action/types'
import { resolveElementRegistryFormId } from '@/common/utils/resolveElementRegistryFormId'
import {
  coalesceEmptyUniTable,
  stripLeakedFormRows,
} from '@/common/utils/coalesceEmptyUniDataTableField'
import { shouldSkipEmptyStateForElement } from '@/common/utils/emptyElementStateGuard'
import {
  logAttachmentStoreDebug,
  summarizeAttachmentFieldEntry,
} from '@/common/utils/attachmentDebugLog'
import {
  markAttachmentStoreMutationCause,
} from '@/common/utils/attachmentStoreMutationCause'
import { useAppStore } from '@/stores/app'
import { IKey } from '@/common/constants/ikey'

/** Есть ли у поля данные для отображения (не вызывать empty() поверх заполненного store) */
function fieldHasPresentableValue(fieldData: any): boolean {
  if (fieldData == null) return false
  if (typeof fieldData !== 'object' || Array.isArray(fieldData)) {
    return fieldData !== '' && fieldData !== undefined
  }
  if ('type' in fieldData && 'value' in fieldData) {
    const v = (fieldData as { value: unknown }).value
    if (v == null) return false
    if (typeof v === 'string') return v.length > 0
    if (typeof v === 'number' || typeof v === 'boolean') return true
    if (Array.isArray(v)) return v.length > 0
    if (typeof v === 'object' && v !== null) {
      const rows = (v as { rows?: unknown }).rows
      if (Array.isArray(rows)) return rows.length > 0
      const t = (v as { _text?: unknown })._text
      if (t != null && String(t).length > 0) return true
      return Object.keys(v).length > 0
    }
    return false
  }
  return Object.keys(fieldData).length > 0
}
interface ElementStyleData {
  textFont?: TextFontStyle
  backgroundColor?: BackgroundColorStyle
}

/**
 * Глобальное хранилище стилей
 */
const elementStylesStore = reactive<Record<string, ElementStyleData>>({})

/**
 * Хранилище отложенных состояний для действий (применяются когда действия регистрируются)
 */
const pendingActionStates = reactive<Map<string, ElementState[]>>(new Map())
const processStatesInflight = new Set<string>()
const pendingElementStates = reactive<Map<string, Array<{
  state: ElementState
  fid: string
  contextFormId?: string
}>>>(new Map())

function clearPendingElementRetryForSlot(fid: string): void {
  const timer = pendingElementRetryTimers.get(fid)
  if (timer) {
    clearTimeout(timer)
    pendingElementRetryTimers.delete(fid)
  }
  pendingElementRetryCounts.delete(fid)
}

/** Clear module-level form state queues when a dialog/page slot closes  */
export function clearFormRuntimeStateForSlot(slotId: string): void {
  const slot = String(slotId ?? '').trim()
  if (!slot) return

  pendingActionStates.delete(slot)
  for (const key of [...pendingActionStates.keys()]) {
    if (key.startsWith(`${slot}:`) || key.endsWith(`:${slot}`)) {
      pendingActionStates.delete(key)
    }
  }

  pendingElementStates.delete(slot)
  lastChangedFieldsOverlayBySlot.delete(slot)
  clearPendingElementRetryForSlot(slot)

  for (const key of [...processStatesInflight]) {
    if (key === slot || key.startsWith(`${slot}:`)) {
      processStatesInflight.delete(key)
    }
  }
}

export function seedPendingActionStateForTest(key: string, states: ElementState[]): void {
  pendingActionStates.set(String(key).trim(), states)
}

export function hasPendingActionStateForTest(key: string): boolean {
  return pendingActionStates.has(String(key).trim())
}

const waitForProcessStatesInflight = async (inflightKey: string, attempts = 20): Promise<boolean> => {
  for (let i = 0; i < attempts; i++) {
    if (!processStatesInflight.has(inflightKey)) return true
    await new Promise<void>((resolve) => setTimeout(resolve, 25))
  }
  return !processStatesInflight.has(inflightKey)
}


type FormStatesProvider = {
  refillElement?: (elementName: string, fieldData: unknown, formId?: string) => void
  setVisible?: (
    name: string,
    visible: boolean,
    scope?: { formId?: string; panelId?: string },
  ) => void
  /** States с element_id: setVisible(level, id) — как в Swing, независимо от registry key Label. */
  setVisibleByLevel?: (
    level: string,
    id: string | number,
    visible: boolean,
    options?: { propagateChildren?: boolean },
  ) => void
  /** Client shell visibility by XML element id — guard against stale is_visible:false. */
  elementShellVisibleByElementId?: Ref<Record<string, boolean>>
  /** persist:false — не писать states повторно (processStates). */
  setEditable?: (name: string, editable: boolean, options?: { persist?: boolean }) => void
  /** persist:false — state уже в jsResult.states (processStates / replay). */
  setEditableByLevel?: (
    level: string,
    id: string | number,
    editable: boolean,
    options?: { persist?: boolean },
  ) => void
  setEnable?: (name: string, enabled: boolean) => void
  requestFocus?: (name: string) => void
  empty?: (name: string) => void
  getElements?: () => Record<string, unknown>
  getActions?: () => unknown
  getForm?: (args: Record<string, unknown>) => Record<string, unknown>
  collectLiveFieldOverrides?: (formId?: string | number) => Record<string, unknown>
  openSuggesterElement?: (name: string, registryFormId?: string) => void
  setElementError?: (name: string, error: string | null) => void
  removeTextFieldBorder?: (name: string) => void
  setCursorPositionToEnd?: (name: string) => void
  expandAllElement?: (name: string) => void
  setMultipleLimitElement?: (name: string, limit: number) => void
  setAutocompleteElement?: (name: string, mode: string) => void
  setEnableSuggestEditElement?: (name: string, enabled: boolean) => void
  setHasEditorElement?: (name: string, hasEditor: boolean) => void
  setMultipleDelimiterElement?: (name: string, delimiter: string) => void
  appendBytesElement?: (name: string, fileName: string, base64Data: string) => void
  appendFileCopiesElement?: (name: string, attachments: unknown) => void
  setVerifyRegexp?: (name: string, regexp: string) => void
  reloadTreeTableNode?: (
    name: string,
    rowNum: number,
    needReloadParent?: boolean,
    selectedNodeData?: unknown,
  ) => void | Promise<void>
}

export function useFormStates(options?: { formProvider?: FormStatesProvider | null }) {
  const getPendingElementStateKey = (fid: string, contextFormId?: string | number | null) =>
    String(contextFormId ?? fid).trim() || String(fid).trim()

  const clearPendingElementRetry = (fid: string) => {
    const timer = pendingElementRetryTimers.get(fid)
    if (timer) {
      clearTimeout(timer)
      pendingElementRetryTimers.delete(fid)
    }
    pendingElementRetryCounts.delete(fid)
  }

  const schedulePendingElementRetry = (fid: string) => {
    const key = String(fid ?? '').trim()
    if (!key) return

    const currentTimer = pendingElementRetryTimers.get(key)
    if (currentTimer) {
      clearTimeout(currentTimer)
    }

    const attempt = (pendingElementRetryCounts.get(key) ?? 0) + 1
    pendingElementRetryCounts.set(key, attempt)

    const delayMs = Math.min(150 * attempt, 1000)
    const timer = setTimeout(async () => {
      pendingElementRetryTimers.delete(key)

      const currentFormProvider = formProvider as any
      const pendingStates = pendingElementStates.get(key)
      const registeredElements = currentFormProvider?.getElements?.()

      if (!pendingStates || pendingStates.length === 0) {
        clearPendingElementRetry(key)
        return
      }

      if (registeredElements && typeof registeredElements === 'object') {
        for (const registeredElement of Object.values(registeredElements as Record<string, any>)) {
          if (!registeredElement || typeof registeredElement !== 'object') continue
          if (String((registeredElement as any).formId ?? '').trim() !== key) continue

          const registeredElementId = (registeredElement as any).elementId
          const registeredElementName =
            String((registeredElement as any).fieldName ?? (registeredElement as any).name ?? '').trim()

          if (!registeredElementName || registeredElementId == null) continue
          await applyPendingElementStates.call(
            currentFormProvider,
            registeredElementId,
            registeredElementName,
            key,
          )
        }
      }

      if ((pendingElementStates.get(key)?.length ?? 0) === 0) {
        clearPendingElementRetry(key)
        return
      }

      if (attempt >= 20) {
        pendingElementStates.delete(key)
        clearPendingElementRetry(key)
        return
      }
      schedulePendingElementRetry(key)
    }, delayMs)

    pendingElementRetryTimers.set(key, timer)
  }

  const enqueuePendingElementState = (
    state: ElementState,
    fid: string,
    contextFormId?: string | number | null,
  ) => {
    const key = getPendingElementStateKey(fid, contextFormId)
    const current = pendingElementStates.get(key) ?? []
    const withoutSame = current.filter((entry) =>
      !(
        entry.state.element_id === state.element_id &&
        (entry.state.element_name ?? '') === (state.element_name ?? '') &&
        entry.fid === fid &&
        String(entry.contextFormId ?? '') === String(contextFormId ?? '')
      )
    )
    withoutSame.push({
      state,
      fid,
      contextFormId: contextFormId != null ? String(contextFormId) : undefined,
    })
    pendingElementStates.set(key, withoutSame)
    schedulePendingElementRetry(key)
  }

  const enqueuePendingActionState = (
    state: ElementState,
    fid: string,
    contextFormId?: string | number | null,
  ) => {
    const key = getPendingElementStateKey(fid, contextFormId)
    const current = pendingActionStates.get(key) ?? []
    const withoutSame = current.filter(
      (entry) =>
        !(
          actionIdsEqual(entry.element_id, state.element_id) &&
          (entry.element_name ?? '') === (state.element_name ?? '')
        ),
    )
    withoutSame.push(state)
    pendingActionStates.set(key, withoutSame)
  }

  const formProvider = options?.formProvider ?? inject(FormProviderKey, null)
  const { pageStates } = usePageData()
  const appStore = useAppStore()

  /**
   * Установить стили для элемента
   */
  const setElementStyle = (elementType: string, elementId: number, styles: ElementStyleData): void => {
    const key = `${elementType.toUpperCase()}_${elementId}`
    const existing = elementStylesStore[key] || {}
    const filteredUpdates = Object.fromEntries(
      Object.entries(styles).filter(([, value]) => value !== undefined)
    )
    elementStylesStore[key] = { ...existing, ...filteredUpdates }
  }

  /**
   * Получить стили элемента
   */
  const getElementStyle = (elementType: string, elementId: number): ElementStyleData | undefined => {
    const key = `${elementType.toUpperCase()}_${elementId}`
    return elementStylesStore[key]
  }
  /**
   * Сгенерировать CSS объект из стилей
   */
  const generateCSSProperties = (styles: ElementStyleData | undefined): Record<string, string> => {
    if (!styles) return {}

    const css: Record<string, string> = {}

    if (styles.textFont) {
      const font = styles.textFont
      if (font.name) {
        css.fontFamily = font.name
      }
      if (font.size) {
        css.fontSize = `${font.size}px`
      }
      if (font.bold || font.isBold) {
        css.fontWeight = 'bold'
      }
      if (font.italic || font.isItalic) {
        css.fontStyle = 'italic'
      }
      if (font.color?.hex) {
        css.color = font.color.hex
      } else if (font.color && (font.color.r !== undefined || font.color.g !== undefined || font.color.b !== undefined)) {
        css.color = `rgb(${font.color.r ?? 0}, ${font.color.g ?? 0}, ${font.color.b ?? 0})`
      }
    }

    if (styles.backgroundColor) {
      const bg = styles.backgroundColor
      if (bg.hex) {
        css.backgroundColor = bg.hex
      } else if (bg.r !== undefined || bg.g !== undefined || bg.b !== undefined) {
        css.backgroundColor = `rgb(${bg.r ?? 255}, ${bg.g ?? 255}, ${bg.b ?? 255})`
      }
    }

    return css
  }

  /**
   * Найти имя элемента по element_id и element_level в структуре формы
   */
  const findElementNameById = (elementId: number, elementType: string, formId: string): string | null => {
    const pageData = appStore.pagesData[formId]
    if (!pageData) {
      return null
    }
    const logicalFormId = resolveElementRegistryFormId(pageData as any, String(formId))
    const targetType = elementType?.toUpperCase()
    const currentFormProvider = formProvider as any
    const registeredElements = currentFormProvider?.getElements?.()

    if (registeredElements && typeof registeredElements === 'object') {
      for (const [registeredName, registeredElement] of Object.entries(registeredElements as Record<string, any>)) {
        if (!registeredElement || typeof registeredElement !== 'object') continue

        const registeredElementIdRaw = (registeredElement as any).elementId
        const registeredElementId =
          typeof registeredElementIdRaw === 'number'
            ? registeredElementIdRaw
            : typeof registeredElementIdRaw === 'string'
              ? parseInt(registeredElementIdRaw, 10)
              : NaN
        if (!actionIdsEqual(registeredElementId, elementId)) continue

        const registeredFormId = String((registeredElement as any).formId ?? '')
        if (registeredFormId && registeredFormId !== logicalFormId) continue

        const registeredElementType = String(
          (registeredElement as any).elementType ??
          (typeof (registeredElement as any).getType === 'function' ? (registeredElement as any).getType() : '') ??
          ''
        ).toUpperCase()

        if (
          targetType === '' ||
          targetType === 'ELEMENT' ||
          registeredElementType === '' ||
          registeredElementType === targetType
        ) {
          const fieldName = String((registeredElement as any).fieldName ?? '').trim()
          return fieldName || registeredName
        }
      }
    }

    const formData = pageData?.form ?? appStore?.pagesData[formId]
    // Поиск в структуре формы
    if (formData) {
      // Поиск действий (actions)
      if (targetType === 'ACTION') {
        const actions = formData.actions?.action
        if (actions) {
          const actionsArray = Array.isArray(actions) ? actions : [actions]
          for (const action of actionsArray) {
            const actionId = action._attributes?.id ? parseInt(action._attributes.id) : null
            if (actionId === elementId) {
              return action?.name?._text ?? ''
            }
          }
        }
      }

      // Поиск overlay-страниц (SwingOverlayPage)
      if (targetType === 'OVERLAYPAGE') {
        const layoutRoot =
          formData?.overlays != null
            ? formData
            : formData?.form?.overlays != null
              ? formData.form
              : null
        const overlays = layoutRoot?.overlays
        if (Array.isArray(overlays)) {
          for (const overlay of overlays) {
            const overlayId = overlay.id ? parseInt(String(overlay.id), 10) : NaN
            if (overlayId === elementId) {
              return overlay.name ?? String(overlayId)
            }
          }
        }
      }

      // Поиск страниц (pages)
      if (targetType === 'PAGE') {
        const searchPages = (pages: any[]): string | null => {
          if (!Array.isArray(pages)) return null
          for (const page of pages) {
            // Pages are parsed with direct 'id' property, not '_attributes.id'
            const pageId = page.id ? parseInt(page.id) : (page._attributes?.id ? parseInt(page._attributes.id) : null)
            if (pageId === elementId) {
              return pageId.toString()
            }
          }
          return null
        }

        // main
        if (formData.main && Array.isArray(formData.main)) {
          for (const mainTab of formData.main) {
            if (Array.isArray(mainTab)) {
              for (const page of mainTab) {
                // Pages are parsed with direct 'id' property
                const pageId = page.id ? parseInt(page.id) : (page._attributes?.id ? parseInt(page._attributes.id) : null)
                if (pageId === elementId) {
                  return pageId.toString()
                }
              }
            } else {
              // Single page in mainTab
              const pageId = mainTab?.id ? parseInt(mainTab.id) : (mainTab?._attributes?.id ? parseInt(mainTab._attributes.id) : null)
              if (pageId === elementId) {
                return pageId.toString()
              }
            }
          }
        }

        // header
        if (formData.header && Array.isArray(formData.header)) {
          const result = searchPages(formData.header)
          if (result) return result
        }

        // footer
        if (formData.footer && Array.isArray(formData.footer)) {
          const result = searchPages(formData.footer)
          if (result) return result
        }
      }

      const panelsMap = formData.panels
      if (!panelsMap || typeof panelsMap !== 'object' || Array.isArray(panelsMap)) {
        return null
      }

      for (const [panelId, panelData] of Object.entries(panelsMap)) {
        if (!panelData || typeof panelData !== 'object') continue

        if (targetType === 'PANEL') {
          const panelIdNum = typeof panelId === 'string' ? parseInt(panelId) : panelId
          if (panelIdNum === elementId) {
            return panelId
          }
        }

        // Ищем элементы в панели
        for (const rowItem of ((panelData as any)?.rows ?? [])) {

          for (const elementItem of rowItem) {
            if (!elementItem?.element) continue

            const config = elementItem.element
            const configId = config._attributes?.id ? parseInt(config._attributes.id) : null
            if (configId === elementId) {
              // Когда type пустой (сервер не передал), считаем элемент подходящим по id
              const elType = config.type?._text?.toUpperCase() || ''
              if (targetType === 'ELEMENT' || targetType === '' || elType === targetType) {
                return (
                  config?.name?._text ??
                  config._attributes?.name ??
                  (config._attributes?.id != null ? String(config._attributes.id) : '')
                )
              }
            }
          }
        }
      }
    }

    return null
  }

  const hasRegisteredElementName = (elementName: string, formId: string): boolean => {
    const normalizedName = String(elementName ?? '').trim()
    if (!normalizedName) return false

    const currentFormProvider = formProvider as any
    const registeredElements = currentFormProvider?.getElements?.() as Record<string, any> | undefined
    if (!registeredElements || typeof registeredElements !== 'object') return false

    const pageData = appStore.pagesData[formId]
    const logicalFormId = resolveElementRegistryFormId(pageData as any, String(formId))

    const matchesScope = (registeredElement: { formId?: unknown }): boolean => {
      const registeredFormId = String(registeredElement.formId ?? '').trim()
      if (!registeredFormId) return true
      return (
        !logicalFormId ||
        registeredFormId === logicalFormId ||
        registeredFormId === String(formId)
      )
    }

    const direct = registeredElements[normalizedName]
    if (direct && matchesScope(direct)) return true

    for (const [registryKey, entry] of Object.entries(registeredElements)) {
      if (!entry || typeof entry !== 'object') continue
      if (!matchesScope(entry as { formId?: unknown })) continue
      const fieldName = String((entry as { fieldName?: unknown }).fieldName ?? '').trim()
      if (fieldName === normalizedName) return true
      if (registryKey === normalizedName) return true
      if (registryKey.startsWith(`${normalizedName}#`)) return true
    }

    return false
  }

  const isDataOnlyFieldState = (
    state: ElementState,
    formId: string,
    pageData: Record<string, any> | null | undefined,
  ): boolean => {
    const directName = String(state.element_name ?? '').trim()
    if (!directName) return false
    if (hasRegisteredElementName(directName, formId)) return false

    const lookupType = state.element_level || ''
    if (state.element_id != null) {
      const foundById = findElementNameById(state.element_id, lookupType, formId)
      if (foundById) return false
    }

    const mergedFields = mergePageFieldMaps(
      pageData?.currentChainData?.fields?.fields,
      pageData?.jsResult?.changed_data?.fields?.fields,
      pageData?.uniData?.fields?.fields,
    )

    return Object.prototype.hasOwnProperty.call(mergedFields, directName)
  }

  const resolveElementNameByState = async (
    state: ElementState,
    formId: string,
    attempts = 20,
    delayMs = 50,
  ): Promise<string | undefined> => {
    const directName = String(state.element_name ?? '').trim()
    const lookupType = state.element_level || ''
    for (let attempt = 0; attempt < attempts; attempt++) {
      if (directName && hasRegisteredElementName(directName, formId)) {
        return directName
      }
      const foundName = findElementNameById(state.element_id, lookupType, formId)
      if (foundName) {
        return foundName
      }
      if (directName && state.element_id == null) {
        return directName
      }
      await nextTick()
      await new Promise<void>((resolve) => setTimeout(resolve, delayMs))
    }

    if (directName && state.element_id == null) {
      return directName
    }
    return undefined
  }

  const applyCustomProperties = (
    properties: CustomPropertyType,
    elementName: string,
    registryFormId?: string,
  ) => {
    for (const prop of properties) {
      switch (prop.property_name) {
        case CustomPropertiesKeys.SET_ERROR:
          if (formProvider?.setElementError) {
            const propertyValue = prop?.property_values?.[0]
            if (propertyValue)
              formProvider.setElementError(elementName, propertyValue?.value ?? null)
          }
          break
        case CustomPropertiesKeys.REMOVE_TEXT_FIELD_BORDER:
          if (formProvider?.removeTextFieldBorder) formProvider.removeTextFieldBorder(elementName)
          break
        case CustomPropertiesKeys.SET_CURSOR_POSITION_TO_END:
          if (formProvider?.setCursorPositionToEnd) formProvider.setCursorPositionToEnd(elementName)
          break
        case CustomPropertiesKeys.EXPAND_ALL:
          if (formProvider?.expandAllElement) formProvider.expandAllElement(elementName)
          break
        case CustomPropertiesKeys.MULTIPLE_LIMIT: {
          const raw = prop.property_values?.[0]?.value
          const n = parseInt(String(raw ?? ''), 10)
          if (formProvider?.setMultipleLimitElement && Number.isFinite(n)) {
            formProvider.setMultipleLimitElement(elementName, n)
          }
          break
        }
        case CustomPropertiesKeys.AUTO_COMPLETE: {
          if (formProvider?.setAutocompleteElement) {
            const raw = prop.property_values?.[0]?.value
            formProvider.setAutocompleteElement(elementName, String(raw ?? ''))
          }
          break
        }
        case CustomPropertiesKeys.ENABLE_SUGGEST_EDIT: {
          if (formProvider?.setEnableSuggestEditElement) {
            const raw = prop.property_values?.[0]?.value
            formProvider.setEnableSuggestEditElement(
              elementName,
              String(raw ?? '').toLowerCase() === 'true',
            )
          }
          break
        }
        case CustomPropertiesKeys.HAS_EDITOR: {
          if (formProvider?.setHasEditorElement) {
            const raw = prop.property_values?.[0]?.value
            formProvider.setHasEditorElement(
              elementName,
              String(raw ?? '').toLowerCase() === 'true',
            )
          }
          break
        }
        case CustomPropertiesKeys.OPEN_SUGGESTER: {
          formProvider?.openSuggesterElement?.(elementName, registryFormId)
          break
        }
        case CustomPropertiesKeys.MULTIPLE_DELIMITER: {
          if (formProvider?.setMultipleDelimiterElement) {
            const raw = prop.property_values?.[0]?.value
            formProvider.setMultipleDelimiterElement(elementName, String(raw ?? ''))
          }
          break
        }
        case CustomPropertiesKeys.APPEND_BYTES: {
          if (formProvider?.appendBytesElement) {
            const fileName = String(prop.property_values?.[0]?.value ?? '')
            const base64Data = String(prop.property_values?.[1]?.value ?? '')
            // добавление к форме
            formProvider.appendBytesElement(elementName, fileName, base64Data)
          }
          break
        }
        case CustomPropertiesKeys.APPEND_FILE_COPIES: {
          if (formProvider?.appendFileCopiesElement) {
            const attachments = prop.property_values?.[0]?.value
            // добавление к форме
            formProvider.appendFileCopiesElement(elementName, attachments)
          }
          break
        }
        case CustomPropertiesKeys.VERIFY_REGEXP: {
          if (formProvider?.setVerifyRegexp) {
            const raw = prop.property_values?.[0]?.value
            formProvider.setVerifyRegexp(elementName, String(raw ?? ''))
          }
          break
        }
        case CustomPropertiesKeys.RELOAD_NODE: {
          if (formProvider?.reloadTreeTableNode) {
            const pv = prop.property_values ?? []
            const rowNum =
              pv[0] !== undefined && pv[0] !== null ? Number(pv[0].value ?? pv[0]) : -1
            const needReloadParent =
              pv[1] !== undefined && pv[1] !== null ? Boolean(pv[1].value ?? pv[1]) : false
            const selectedNodeData = pv[2] !== undefined ? pv[2].value ?? pv[2] : undefined
            void formProvider.reloadTreeTableNode(
              elementName,
              Number.isFinite(rowNum) ? rowNum : -1,
              needReloadParent,
              selectedNodeData,
            )
          }
          break
        }
        default:
          console.warn(`[useFormStates] Unknown custom property: ${prop.property_name}`)
      }
    }
  }

  /**
   * Применить состояние к элементу формы
   */
  const applyElementState = async (
    state: ElementState,
    formId: string,
    contextFormId?: string | number | null,
    directChangedFields?: Record<string, any>,
    /** true только при merge по onCloseDialogParentMergeFieldKeys */
    strictDirectFieldsAllowlist?: boolean,
  ) => {
    const {
      element_id,
      element_level,
      element_name,
      is_visible,
      is_editable,
      is_enabled,
      is_focused,
      is_refill,
      is_empty,
      text_font,
      background_color,
      remove_text_field_border
    } = state

    const elementType = (element_level != null && typeof element_level === 'string') ? element_level : ''
    const stateElementType = String((state as any).element_type ?? '').trim().toUpperCase()
    const fid = String(formId)
    const pd = appStore.pagesData as Record<string | number, any>
    const pageData = pd[fid] ?? pd[formId as any]
    if (!pageData) {
      console.warn(`[useFormStates] PageData not found for formId: ${formId}`)
      return
    }

    /** Id формы для реестра элементов */
    const contextRegistryFormId = String(contextFormId ?? '').trim()
    const registryFormId = contextRegistryFormId || resolveElementRegistryFormId(pageData as any, fid)

    const actionsRegistry = formProvider && (formProvider as any).getActions ? (formProvider as any).getActions() : null
    const actionById = resolveActionRegistryEntry(actionsRegistry, element_id, element_name)
    const treatAsAction =
      elementType.toUpperCase() === 'ACTION' ||
      element_level?.toUpperCase() === 'ACTION' ||
      (elementType === '' && actionById)
    if (treatAsAction && !actionById) {
      if (isHideOnlyUnregisteredActionState(state)) {
        return
      }
      if (isNonRegistryLayoutActionState(state, pageData?.form)) {
        return
      }
      enqueuePendingActionState(state, fid, contextFormId)
      return
    }
    if (treatAsAction && actionById) {
      try {
        const actionTitle =
          typeof (state as any).title === 'string'
            ? (state as any).title
            : (state as any).title?._text
        if (actionTitle != null && typeof actionById.setTitle === 'function') {
          ;(actionById.setTitle as (t: string) => void)(actionTitle)
        }
        const visible = coerceStateBoolean(is_visible)
        if (visible !== undefined && typeof actionById.setVisible === 'function') {
          ;(actionById.setVisible as (v: boolean) => void)(visible)
        }
        const editable = coerceStateBoolean(is_editable)
        if (editable !== undefined && typeof actionById.setEditable === 'function') {
          ;(actionById.setEditable as (v: boolean) => void)(editable)
        }
        const enabled = coerceStateBoolean(is_enabled)
        if (enabled !== undefined && typeof (actionById as { setEnable?: (v: boolean) => void }).setEnable === 'function') {
          ;((actionById as { setEnable: (v: boolean) => void }).setEnable)(enabled)
        }
        return
      } catch (error) {
        console.error(`[useFormStates] Error applying state to action id=${element_id}:`, error)
        return
      }
    }

    const upperLevel = (element_level ?? elementType ?? '').toString().toUpperCase()
    if (upperLevel === 'OVERLAYPAGE') {
      let overlayName =
        element_name != null && String(element_name).trim() !== ''
          ? String(element_name).trim()
          : null
      if (!overlayName && element_id != null) {
        overlayName = findElementNameById(element_id, 'OVERLAYPAGE', fid)
      }
      if (overlayName && is_visible !== undefined && formProvider?.setVisible) {
        formProvider.setVisible(overlayName, is_visible)
      }
      return
    }

    if (upperLevel === 'FORM' || upperLevel === 'PAGE') {
      return
    }

    const elementNameEarly = String(element_name ?? '').trim()
    const pageStates = Array.isArray(pageData?.jsResult?.states) ? pageData.jsResult.states : []
    const coalescedElementState =
      element_id != null && Number.isFinite(Number(element_id))
        ? findElementPageState(pageStates, Number(element_id), elementNameEarly)
        : undefined

    // Видимость по platform id до resolveElementNameByState — Label регистрируется позже onMounted.
    // Только is_visible с текущего state: coalesced+is_refill иначе поднимает stale open-dialog false.
    if (element_id != null) {
      const fp = formProvider as FormStatesProvider
      const visibleEarly = coerceStateBoolean(is_visible)
      if (visibleEarly !== undefined && typeof fp?.setVisibleByLevel === 'function') {
        const shellVisible = fp.elementShellVisibleByElementId?.value?.[String(element_id)]
        const applyIncoming = shouldApplyIncomingElementVisibility({
            incomingVisible: visibleEarly,
            shellVisible,
            isRefill: coerceStateBoolean(is_refill) === true,
            isEmpty: coerceStateBoolean(is_empty) === true,
          })
        if (applyIncoming) {
          const level = String(element_level ?? 'element').trim().toLowerCase() || 'element'
          // States already list per-element visibility (onLoad setVisible). Do not
          // panel→children propagate — that force-shows sibling Labels and then shell
          // guard blocks hide.
          fp.setVisibleByLevel(level, element_id, visibleEarly, { propagateChildren: false })
        }
      }
    }

    // PANEL is_editable: Swing JSContext cascades only if the panel flag changed.
    // Do not setEditable(name) — that stamps every child and overrides XML isReadOnly (WUI-573).
    // persist:false — state already in jsResult.states; do not echo persist from processStates.
    if (upperLevel === 'PANEL') {
      const fp = formProvider as FormStatesProvider
      const editable = coerceStateBoolean(is_editable)
      if (
        editable !== undefined &&
        element_id != null &&
        typeof fp?.setEditableByLevel === 'function'
      ) {
        fp.setEditableByLevel('panel', element_id, editable, { persist: false })
      }
      return
    }

    const elementName: string | undefined = await resolveElementNameByState(state, fid)

    if (!elementName) {
      if (isDataOnlyFieldState(state, fid, pageData as Record<string, any>)) {
        return
      }
      // Не логируем как предупреждение — элемент может ещё не быть зарегистрирован
      if (elementType !== '' || element_level !== '' || (element_name != null && element_name !== '')) {
        console.warn(
          `[useFormStates] Element not found: id=${element_id}, type=${elementType || element_level}, slotId=${fid}, registryFormId=${registryFormId}, element_name=${element_name}`,
        )
      }
      enqueuePendingElementState(state, fid, contextFormId)
      return
    }

    if (stateElementType === 'FILEATTACHMENTS') {
      logAttachmentStoreDebug('states-apply', {
        elementName,
        elementId: element_id,
        is_refill: coerceStateBoolean(is_refill),
        is_empty: coerceStateBoolean(is_empty),
        stateElementType,
      })
    }

    try {
      // Apply visibility for non-action elements (по имени — если element_id не было в state).
      if (is_visible !== undefined && element_id == null) {
        const fp = formProvider as FormStatesProvider
        const visible = coerceStateBoolean(is_visible)
        if (visible !== undefined && fp?.setVisible) {
          const stateScope =
            contextFormId != null && String(contextFormId).trim() !== ''
              ? { formId: String(contextFormId) }
              : undefined
          fp.setVisible(elementName, visible, stateScope)
        }
      }


      const coalescedForShell =
        findElementPageState(pageStates, Number(element_id ?? 0), String(elementName ?? '')) ??
        coalescedElementState
      const editable = coerceStateBoolean(coalescedForShell?.is_editable)
      const visibleForEditable = coerceStateBoolean(coalescedForShell?.is_visible)
      if (editable !== undefined && formProvider?.setEditable) {
        // hide-pairing is_editable:false с is_visible:false не трогаем (после show — XML / явный setEditable).
        if (!(editable === false && visibleForEditable === false)) {
          // persist:false: ELEMENT уже в jsResult.states; live persist оставить только скрипту.
          formProvider.setEditable(elementName, editable, { persist: false })
        }
      }

      const enabled = coerceStateBoolean(is_enabled)
      if (enabled !== undefined && formProvider?.setEnable) {
        formProvider.setEnable(elementName, enabled)
      }

      const refillFlag = coerceStateBoolean(is_refill)
      if (refillFlag === true && formProvider?.refillElement && elementName !== IKey.ELEMENT_STATES) {
        if (
          strictDirectFieldsAllowlist === true &&
          directChangedFields != null &&
          typeof directChangedFields === 'object' &&
          !Array.isArray(directChangedFields) &&
          !Object.prototype.hasOwnProperty.call(directChangedFields, elementName)
        ) {
          // Срез по allowlist закрытия диалога: поля нет в payload
        } else {
        // Как в mergePageDataFieldSnapshot: changed_data важнее устаревшего снимка uniData.
        const mergedFields = mergePageFieldMaps(
          pageData?.uniData?.fields?.fields,
          pageData?.currentChainData?.fields?.fields,
          pageData?.jsResult?.changed_data?.fields?.fields,
        )
        const overlay = lastChangedFieldsOverlayBySlot.get(fid)
        const registeredElement = (formProvider as any)?.getElements?.()?.[elementName] as
          | { elementType?: string; getType?: () => string }
          | undefined
        const registeredElementType = String(
          registeredElement?.getType?.() ?? registeredElement?.elementType ?? '',
        )
          .trim()
          .toUpperCase()
        const isFileAttachments = registeredElementType === 'FILEATTACHMENTS'
        const shouldSkipTableCoalesce =
          stateElementType === 'MULTISELECT' || registeredElementType === 'MULTISELECT'

        if (!shouldSkipTableCoalesce) {
          coalesceEmptyUniTable(mergedFields, elementName, overlay ? [overlay] : undefined)
        }
        const directFlat = directChangedFields as Record<string, any> | undefined
        const directElementData =
          directFlat && Object.prototype.hasOwnProperty.call(directFlat, elementName)
            ? directFlat[elementName]
            : undefined
        const overlayElementData =
          overlay && Object.prototype.hasOwnProperty.call(overlay, elementName)
            ? overlay[elementName]
            : undefined
        const elementData =
          directElementData !== undefined
            ? directElementData
            : overlayElementData !== undefined
              ? overlayElementData
              : mergedFields[elementName]

        // Extract value from UniData format if needed
        let fieldDataForRefill: any
        if (elementData && typeof elementData === 'object' && !Array.isArray(elementData) && 'value' in elementData && 'type' in elementData) {
          // Already in UniData format, use as-is
          fieldDataForRefill = elementData
        } else if (elementData !== undefined && elementData !== null) {
          // Plain value, wrap it in UniData format
          fieldDataForRefill = {
            type: typeof elementData === 'string' ? 'String' :
              typeof elementData === 'number' ? (Number.isInteger(elementData) ? 'Long' : 'Double') :
                typeof elementData === 'boolean' ? 'Boolean' :
                  Array.isArray(elementData) ? 'Array' : 'Object',
            value: elementData
          }
        } else {
          // elementData is undefined or null - pass null value
          fieldDataForRefill = {
            type: 'String',
            value: null
          }
        }

        if ((fieldDataForRefill as any)?.type === 'UniDataTable') {
          fieldDataForRefill = stripLeakedFormRows(fieldDataForRefill) as typeof fieldDataForRefill
        }

        const rowsLen = Array.isArray((fieldDataForRefill as any)?.value?.rows)
          ? (fieldDataForRefill as any).value.rows.length
          : null

        let reg = (formProvider as any)?.getElements?.()?.[elementName] as { formId?: unknown } | undefined
        if (!reg && formProvider) {
          const maxAttempts = 20
          for (let attempt = 0; attempt < maxAttempts && !reg; attempt++) {
            await nextTick()
            await new Promise<void>((r) => setTimeout(r, 50))
            reg = (formProvider as any)?.getElements?.()?.[elementName]
          }
        }
        if (!reg) {
          if (stateElementType === 'FILEATTACHMENTS') {
            logAttachmentStoreDebug('states-refill-pending-no-registry', {
              elementName,
              elementId: element_id,
              refillFlag,
              rowsLen,
              payload: summarizeAttachmentFieldEntry(fieldDataForRefill),
            })
          }
          enqueuePendingElementState(state, fid, contextFormId)
          return
        }
        const refillRegistryId =
          reg != null && reg.formId != null && String(reg.formId).trim() !== ''
            ? String(reg.formId)
            : registryFormId

        const regPanelId = String((reg as { panelId?: unknown })?.panelId ?? '').trim()
        const regElementId = String((reg as { elementId?: unknown })?.elementId ?? '').trim()
        const readRegistryField = formProvider?.getForm
        const currentRegistryEntry =
          readRegistryField && regPanelId && regElementId
            ? readRegistryField({
                formId: refillRegistryId,
                panelId: regPanelId,
                elementId: regElementId,
                flatten: true,
                pageDataSlot: fid,
              })?.[elementName]
            : undefined
        if (!fieldEntryValueEqual(currentRegistryEntry, fieldDataForRefill)) {
          if (isFileAttachments) {
            logAttachmentStoreDebug('states-refill', {
              cause: 'states-refill',
              elementName,
              elementId: regElementId,
              refillFlag,
              rowsLen,
              payload: summarizeAttachmentFieldEntry(fieldDataForRefill),
              current: summarizeAttachmentFieldEntry(currentRegistryEntry),
            })
          }
          markAttachmentStoreMutationCause({
            fieldName: elementName,
            elementId: regElementId,
            cause: 'states-refill',
            detail: { refillFlag, rowsLen },
          })
          formProvider.refillElement(elementName, fieldDataForRefill, refillRegistryId)
        } else if (isFileAttachments) {
          logAttachmentStoreDebug('states-refill-skipped-equal', {
            elementName,
            elementId: regElementId,
            refillFlag,
            payload: summarizeAttachmentFieldEntry(fieldDataForRefill),
          })
        }
        }
      }


      if (coerceStateBoolean(is_focused) === true && formProvider?.requestFocus) {
        const registeredForFocus = (formProvider as { getElements?: () => Record<string, unknown> })
          ?.getElements?.()?.[elementName]
        if (registeredForFocus) {
          formProvider.requestFocus(elementName)
        }
      }


      if (
        coerceStateBoolean(is_empty) === true &&
        coerceStateBoolean(is_refill) !== true &&
        formProvider?.empty
      ) {
        const merged = mergePageFieldMaps(
          pageData?.uniData?.fields?.fields,
          pageData?.currentChainData?.fields?.fields,
          pageData?.jsResult?.changed_data?.fields?.fields,
        )
        const liveEntry = (formProvider as { collectLiveFieldOverrides?: (formId?: string | number) => Record<string, unknown> })
          .collectLiveFieldOverrides?.(registryFormId)?.[elementName]

        const emptyElementReg = (formProvider as { getElements?: () => Record<string, unknown> })
          ?.getElements?.()?.[elementName] as {
            elementType?: string
            getType?: () => string
            elementId?: string | number
          } | undefined
        const emptyElementType = String(
          emptyElementReg?.getType?.() ?? emptyElementReg?.elementType ?? stateElementType ?? '',
        )
          .trim()
          .toUpperCase()
        const isFileAttachmentsEmpty = emptyElementType === 'FILEATTACHMENTS'

        if (!shouldSkipEmptyStateForElement(merged, elementName, liveEntry)) {
          if (isFileAttachmentsEmpty) {
            logAttachmentStoreDebug('states-empty', {
              cause: 'states-empty',
              elementName,
              is_empty: true,
              is_refill: coerceStateBoolean(is_refill),
              mergedField: summarizeAttachmentFieldEntry(merged[elementName]),
              liveEntry: summarizeAttachmentFieldEntry(liveEntry),
            })
          }
          markAttachmentStoreMutationCause({
            fieldName: elementName,
            elementId: emptyElementReg?.elementId ?? element_id ?? null,
            cause: 'states-empty',
          })
          formProvider.empty(elementName)
        } else if (isFileAttachmentsEmpty) {
          logAttachmentStoreDebug('states-empty-skipped', {
            elementName,
            is_empty: true,
            mergedField: summarizeAttachmentFieldEntry(merged[elementName]),
            liveEntry: summarizeAttachmentFieldEntry(liveEntry),
          })
        }
      }

      // Сохраняем стили/флаги в хранилище (применение через компоненты)
      if (text_font || background_color || remove_text_field_border !== undefined) {
        setElementStyle(elementType, element_id, {
          textFont: text_font,
          backgroundColor: background_color,
        })
      }

      // Обработка custom_properties
      if (state.custom_properties?.length) {
        applyCustomProperties(state.custom_properties, elementName, registryFormId)
      }
    } catch (error) {
      const elementIdentifier = elementType.toUpperCase() === 'ACTION' ? `id=${element_id}` : `name="${elementName}"`
      console.error(`[useFormStates] Error applying state to element ${elementIdentifier}:`, error)
    }
  }

  const processStates = async (
    formId: string,
    dataStates?: ElementState[],
    changedData?: any,
    contextFormId?: string | number | null,
    parentMergeFieldKeys?: string[],
    strictParentMergeAllowlistForRefill?: boolean,
  ) => {
    const fid = String(formId)
    const registryId = contextFormId != null ? String(contextFormId) : fid
    const inflightKey = `${fid}:${registryId}`
    if (processStatesInflight.has(inflightKey)) {
      if (changedData != null) {
        const released = await waitForProcessStatesInflight(inflightKey)
        if (released) {
          return processStates(
            formId,
            dataStates,
            changedData,
            contextFormId,
            parentMergeFieldKeys,
            strictParentMergeAllowlistForRefill,
          )
        }
      }
      if (formProvider?.openSuggesterElement && Array.isArray(dataStates) && dataStates.length > 0) {
        applyOpenSuggesterFromStates(
          dataStates,
          formProvider.openSuggesterElement.bind(formProvider),
          contextFormId != null ? String(contextFormId) : undefined,
        )
      }
      return
    }
    processStatesInflight.add(inflightKey)

    try {
    const states = dataStates ?? pageStates.value ?? []

    if (!Array.isArray(states) || states.length === 0) {
      return
    }

    // If changedData is provided, merge it into pageData before processing states
    // TODO: данные из states НЕ добавляем в store
    if (changedData) {
      const pd = appStore.pagesData as Record<string | number, any>
      const pageData = pd[fid] ?? pd[formId as any]
      const flat = getDialogCloseChangedFieldsFlat(changedData)
      const fieldsData = pickDialogCloseParentMergeFlatFields(
        flat,
        parentMergeFieldKeys === undefined ? undefined : parentMergeFieldKeys,
        states as unknown as StateData[],
      )
      if (fieldsData && typeof fieldsData === 'object' && !Array.isArray(fieldsData)) {
        const prev = lastChangedFieldsOverlayBySlot.get(fid) || {}
        lastChangedFieldsOverlayBySlot.set(fid, {...prev, ...fieldsData})
      }
      if (pageData) {
        const mergedChangedFields = overlayAuthoritativeFieldKeys(
          mergePageFieldMaps(
            pageData.jsResult?.changed_data?.fields?.fields || {},
            fieldsData,
          ),
          fieldsData,
        )
        for (const s of states) {
          if (s.is_refill && s.element_name) {
            const stateElementType = String((s as any).element_type ?? '').trim().toUpperCase()
            if (stateElementType !== 'MULTISELECT') {
              coalesceEmptyUniTable(mergedChangedFields, s.element_name, [fieldsData])
            }
          }
        }
        const updatedPageData = {
          ...pageData,
          jsResult: {
            ...pageData.jsResult,
            changed_data: {
              ...pageData.jsResult?.changed_data,
              fields: {
                ...pageData.jsResult?.changed_data?.fields,
                fields: mergedChangedFields,
              },
            },
          },
        }
        if (!shallowFieldMapEqual(pageData.jsResult?.changed_data?.fields?.fields, mergedChangedFields)) {
          appStore.setPageData(fid, updatedPageData, undefined, undefined, 'useFormStates.processStates.changedData')
        }
        // Wait for reactivity to update
        await new Promise(resolve => setTimeout(resolve, 0))
      }
    }

    const actionStates: ElementState[] = []
    const otherStates: ElementState[] = []

    const pdForActions = appStore.pagesData as Record<string | number, any>
    const pageDataForActions = pdForActions[fid] ?? pdForActions[formId as any]
    const formRootForActions = pageDataForActions?.form

    for (const state of states) {
      if (state.element_level?.toUpperCase() === 'ACTION') {
        if (isNonRegistryLayoutActionState(state, formRootForActions)) {
          continue
        }
        actionStates.push(state)
      } else {
        otherStates.push(state)
      }
    }

    // Process non-action states first (elements, panels, pages)
    const directFlatRaw = getDialogCloseChangedFieldsFlat(changedData)
    const directFlat =
      parentMergeFieldKeys === undefined
        ? directFlatRaw
        : pickDialogCloseParentMergeFlatFields(
            directFlatRaw,
            parentMergeFieldKeys,
            states as unknown as StateData[],
          )
    const directChangedFields =
      directFlat && typeof directFlat === 'object' && Object.keys(directFlat).length > 0
        ? directFlat
        : undefined
    const strictDirectFieldsAllowlist = strictParentMergeAllowlistForRefill === true
    for (const state of otherStates) {
      await applyElementState(
        state,
        fid,
        contextFormId,
        directChangedFields,
        strictDirectFieldsAllowlist,
      )
    }

    // For action states, wait until each action is registered
    if (actionStates.length > 0) {
      await nextTick()
      const formProviderInternal = formProvider as any
      const getActions = formProviderInternal?.getActions
      const pendingKey = getPendingElementStateKey(fid, contextFormId)

      let attempts = 0
      const maxAttempts = 10
      let unresolved = [...actionStates]

      while (attempts < maxAttempts && unresolved.length > 0) {
        const actionsRegistry = getActions ? getActions() : null
        const stillUnresolved: ElementState[] = []

        for (const state of unresolved) {
          const action = resolveActionRegistryEntry(
            actionsRegistry,
            state.element_id,
            state.element_name,
          )
          if (action) {
            await applyElementState(state, fid, contextFormId)
          } else {
            stillUnresolved.push(state)
          }
        }

        unresolved = stillUnresolved
        if (unresolved.length === 0) {
          return
        }

        await new Promise(resolve => setTimeout(resolve, 50))
        attempts++
      }

      if (unresolved.length > 0) {
        const registryPending = unresolved.filter(
          (state) =>
            !isHideOnlyUnregisteredActionState(state) &&
            !isNonRegistryLayoutActionState(state, formRootForActions),
        )
        if (registryPending.length > 0) {
          const current = pendingActionStates.get(pendingKey) ?? []
          pendingActionStates.set(pendingKey, [...current, ...registryPending])
        }
      }
    }

  } finally {
    processStatesInflight.delete(inflightKey)
  }
  }

  /**
   * Применить отложенные состояния для действий (вызывается когда действие регистрируется)
   */
  function applyPendingActionStates(this: any, actionId: number, actionName: string, formId: string) {
    const registryKey = String(formId ?? '').trim()
    let pendingBucketKey = registryKey
    let pendingStates = registryKey ? pendingActionStates.get(registryKey) : undefined
    if (!pendingStates?.length) {
      for (const [key, states] of pendingActionStates.entries()) {
        if (
          states.some(
            (state) =>
              state.element_level?.toUpperCase() === 'ACTION' &&
              actionIdsEqual(state.element_id, actionId),
          )
        ) {
          pendingBucketKey = key
          pendingStates = states
          break
        }
      }
    }
    if (!pendingStates || pendingStates.length === 0) {
      return
    }

    const matchingStates = pendingStates.filter((state) => {
      if (state.element_level?.toUpperCase() !== 'ACTION') return false
      if (actionIdsEqual(state.element_id, actionId)) return true
      const stateId = state.element_id
      if (stateId != null && String(stateId).trim() !== '') return false
      return (
        Boolean(actionName) &&
        String(state.element_name ?? '').trim() === String(actionName).trim()
      )
    })

    if (matchingStates.length > 0) {
      const currentFormProvider = this || formProvider
      if (!currentFormProvider) {
        return
      }

      for (const state of matchingStates) {
        const formProviderInternal = currentFormProvider as any
        const getActions = formProviderInternal?.getActions
        if (getActions) {
          const actionsRegistry = getActions()
          const action = resolveActionRegistryEntry(
            actionsRegistry,
            state.element_id,
            state.element_name ?? actionName,
          )
          if (action) {
            const visible = coerceStateBoolean(state.is_visible)
            if (visible !== undefined && typeof action.setVisible === 'function') {
              ;(action.setVisible as (v: boolean) => void)(visible)
            }
            const editable = coerceStateBoolean(state.is_editable)
            if (editable !== undefined && typeof action.setEditable === 'function') {
              ;(action.setEditable as (v: boolean) => void)(editable)
            }
            const enabled = coerceStateBoolean(state.is_enabled)
            if (enabled !== undefined && typeof (action as { setEnable?: (v: boolean) => void }).setEnable === 'function') {
              ;((action as { setEnable: (v: boolean) => void }).setEnable)(enabled)
            }
          }
        }
      }
      const remainingStates = pendingStates.filter(state => !matchingStates.includes(state))
      if (remainingStates.length > 0) {
        pendingActionStates.set(pendingBucketKey, remainingStates)
      } else {
        pendingActionStates.delete(pendingBucketKey)
      }
    }
  }

  async function applyPendingElementStates(
    this: any,
    elementId: number | string,
    elementName: string,
    formId: string,
  ) {
    const fid = String(formId ?? '').trim()
    if (!fid) return

    const currentFormProvider = this || formProvider
    if (!currentFormProvider) return

    const pendingStates = pendingElementStates.get(fid)
    if (!pendingStates || pendingStates.length === 0) {
      return
    }

    const normalizedElementId = typeof elementId === 'number' ? elementId : parseInt(String(elementId), 10)
    const matchingStates = pendingStates.filter(({ state }) =>
      state.element_level?.toUpperCase() !== 'ACTION' &&
      (
        actionIdsEqual(state.element_id, normalizedElementId) ||
        actionIdsEqual(state.element_id, elementId) ||
        (state.element_name != null && state.element_name === elementName)
      )
    )

    if (matchingStates.length === 0) {
      return
    }

    for (const entry of matchingStates) {
      await applyElementState(entry.state, entry.fid, entry.contextFormId)
    }

    const remainingStates = pendingStates.filter((entry) => !matchingStates.includes(entry))
    if (remainingStates.length > 0) {
      pendingElementStates.set(fid, remainingStates)
      schedulePendingElementRetry(fid)
    } else {
      pendingElementStates.delete(fid)
      clearPendingElementRetry(fid)
    }
  }

  if (formProvider) {
    (formProvider as any).applyPendingActionStates = applyPendingActionStates
    ;(formProvider as any).applyPendingElementStates = applyPendingElementStates
  }

  return {
    processStates,
    applyElementState,
    findElementNameById,
    setElementStyle,
    getElementStyle,
    generateCSSProperties,
    applyPendingActionStates,
    applyPendingElementStates,
  }
}

